import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { InferGetStaticPropsType, GetStaticProps } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'react-i18next';
import Header from '../../header';
import Footer from '../../footer';
import styles from '@/styles/Documents.module.css'
import Search from '../../images/search';
import { Swiper, SwiperSlide } from 'swiper/react';
import "swiper/css";
import { Navigation } from 'swiper';
import Link from 'next/link';
import RightArrow from '../../images/rightArrow';
import LoadingSpinner from '@/components/loading-spinner/loadingSpinner';
import PortalLaptop from '@/pages/images/portalLaptop';
import LoginMethod from '@/pages/images/loginMethod';
import Theme from '@/pages/images/theme';
import NotificationPortal from '@/pages/images/notificationPortal';
import { useRouter } from 'next/router';
import PageSeo from '@/components/seo/PageSeo';


export const markDocumentAsViewed = (documentId: number) => {
  if (typeof window !== "undefined") {
    let viewedDocuments = JSON.parse(localStorage.getItem('serviceManagementViewedDocuments') || '[]');
    if (!viewedDocuments.includes(documentId)) {
      viewedDocuments.push(documentId);
      localStorage.setItem('serviceManagementViewedDocuments', JSON.stringify(viewedDocuments));
    }
  }
};

const debounce = (func: Function, delay: number) => {
  let timeoutId: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

interface SearchResult {
  id: number
  title: string
  description: string
  link?: string
}
interface ServiceDocumentStep {
  id: number;
  icon: React.ComponentType<{ color?: string }>;
  docNumber: string;
  title: string;
  link: string;
  description: string;
}

const Documents = (_props: InferGetStaticPropsType<typeof getStaticProps>) => {
  const searchMenuRef = useRef<HTMLDivElement>(null);

  const { t, i18n } = useTranslation('documentsTranslate');
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(true)
  const [isSearchInputActive, setIsSearchInputActive] = useState(false)
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [results, setResults] = useState<SearchResult[]>([]);

  useEffect(() => {
    const loadTranslations = async () => {
      await i18n.loadNamespaces('documentsTranslate')
      setIsLoading(false)
    }

    loadTranslations()
  }, [t, i18n])

  useEffect(() => {
    const handler = (e: any) => {
      if (searchMenuRef.current && !searchMenuRef.current.contains(e.target)) {
        setIsSearchInputActive(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
    };
  }, []);

  const serviceDocumentStep: ServiceDocumentStep[] = useMemo(() => [
    {
      id: 1,
      icon: PortalLaptop,
      docNumber: 'Doc 1',
      title: `${t("serviceManagementDocPortalTitle")}`,
      link: '/service-management/documents/portal',
      description: t("serviceManagementDocPortalDescriptionOne")
    },
    {
      id: 2,
      icon: LoginMethod,
      docNumber: 'Doc 2',
      title: `${t("serviceManagementDocLoginMethodTitle")}`,
      link: '/service-management/documents/login-method',
      description: t("serviceManagementDocLoginDescriptionOne")
    },
    {
      id: 3,
      icon: Theme,
      docNumber: 'Doc 3',
      title: `${t("serviceManagementDocThemeTitle")}`,
      link: '/service-management/documents/content-theme',
      description: t("serviceManagementDocThemeDescriptionOne")
    },
    {
      id: 4,
      icon: NotificationPortal,
      docNumber: 'Doc 4',
      title: `${t("serviceManagementDocPortalNotificationTitle")}`,
      link: '/service-management/documents/notification',
      description: t("serviceManagamenetDocNotificationDescriptionOne") || t("serviceManagementDocPortalNotificationTitle")
    }
  ], [t]);

  const performSearch = useCallback((term: string) => {
    if (term && term.trim() !== '') {
      const searchTermLower = term.toLowerCase();
      const filteredResults = serviceDocumentStep.flatMap(item => {
        const title = item.title;
        const description = item.description || '';

        if (title.toLowerCase().includes(searchTermLower) || description.toLowerCase().includes(searchTermLower)) {
          return [{
            id: item.id - 1,
            title: title,
            description: description,
            link: item.link
          }];
        }
        return [];
      });
      setResults(filteredResults);
    } else {
      setResults([]);
    }
  }, [serviceDocumentStep]);

  const debouncedSearch = useCallback((term: string) => {
    const debouncedFunc = debounce((term: string) => performSearch(term), 300);
    debouncedFunc(term);
  }, [performSearch]);

  useEffect(() => {
    debouncedSearch(searchTerm);
  }, [searchTerm, debouncedSearch]);

  const highlightTerm = (text: string, term: string) => {
    if (!term) return text;

    const regex = new RegExp(`(${term})`, 'gi');
    const parts = text?.split(regex);

    return parts?.map((part, index) =>
      regex.test(part) ? (
        <span key={index} style={{ backgroundColor: '#00323d', color: "#fff", borderRadius: "5px", padding: "0 1px" }}>{part}</span>
      ) : (
        part
      )
    );
  };

  if (isLoading) {
    return <LoadingSpinner />
  }

  return (
    <>
      <PageSeo
        title={`${router.locale === "tr" ? "Service Management Dokümanları" : "Service Management Documents"} | Stoneity`}
        description={router.locale === "tr"
          ? "Stoneity Service Management dokümanlarını inceleyin; portal, giriş yöntemleri, tema ve bildirim yönetimi konularını keşfedin."
          : "Browse Stoneity Service Management documents covering portal setup, login methods, theme configuration, and notifications."
        }
        locale={router.locale}
        keywords={router.locale === "tr"
          ? "service management dokümanları, servis portalı, bildirim yönetimi, Stoneity"
          : "service management documents, service portal, notification management, Stoneity"
        }
      />
      <Header />
      <div className={styles.mainFlex}>
        <div className={styles.pageFlex} style={{ backgroundColor: "#fff", paddingBottom: "0" }}>
          <div className={styles.documentsWrapper}>
            <div className={styles.documentsTop}>
              <div className={styles.title}>{t("documentsTopTitle")}</div>
              <div className={styles.searchInputWrapper}
                ref={searchMenuRef}
                onClick={() => setIsSearchInputActive(!isSearchInputActive)}>
                <input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} type="text" placeholder={t("search")} className={`${styles.searchInput} ${isSearchInputActive ? styles.searchInputActive : ''}`} />
                <div className={styles.searchIconWrapper}>
                  <Search />
                </div>
                <div className={`${styles.searchInputMenu} ${isSearchInputActive ? styles.searchInputMenuActive : ''}`}>
                  <div className={styles.searchInputMenuTitleWrapper}>
                    <Search color={"#605C5C"} />
                    {t("documentSearchBox")}
                  </div>
                  <div className={styles.searchInputColumnWrapper}>
                    {
                      results.length > 0 ? (<div className={styles.searchResultCardWrapper}>
                        {results.map((result, index) => {
                          return (
                            <Link key={index} className={styles.searchInputColumn} href={result.link || ""} onClick={() => result.link && markDocumentAsViewed(result.id + 1)}>
                              <div className={styles.searchInputColumnLeft}>Doc {(result.id + 1)}</div>
                              <div className={styles.searchInputColumnRight}>{highlightTerm(result.description, searchTerm)}</div>
                            </Link>
                          )
                        })}
                      </div>) : (<p style={{ marginLeft: "15px" }}>{t("noResultFound")}</p>)
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={styles.stepWrapper}>
            {serviceDocumentStep.map((docStep, index) => {
              const viewedDocuments = typeof window !== "undefined" ? JSON.parse(localStorage.getItem('serviceManagementViewedDocuments') || '[]') : [];
              const isViewed = viewedDocuments.includes(docStep.id);
              const IconComponent = docStep.icon;
              return (
                <Link key={index} href={docStep.link} className={styles.stepperItem} style={{ width: "auto", flex: "1 1" }} onClick={() => markDocumentAsViewed(docStep.id)}>
                  <div className={`${styles.stepIconWrapper} ${isViewed ? styles.completed : ""}`}>
                    {IconComponent && <IconComponent color={isViewed ? "#fff" : "#03555E"} />}
                  </div>
                  <div className={styles.stepName}>{docStep.title}</div>
                  <div className={styles.stepDoc}>{docStep.docNumber}</div>
                </Link>
              )
            })}
          </div>
          <div className={styles.documentCardSwiperWrapper}>
            <Swiper
              breakpoints={{
                0: { slidesPerView: 1 },
                900: { slidesPerView: 2 },
                1230: { slidesPerView: 3 },
              }}
              slidesPerView={3}
              spaceBetween={30}
              className={styles.documentCardMySwiper}
              modules={[Navigation]}
              navigation={{
                prevEl: "." + styles.productFeaturesSlideWrapper,
                nextEl: "." + styles.productFeaturesSlideWrapper2,
              }}
            >
              {serviceDocumentStep.map((docStep, index) => {
                const IconComponent = docStep.icon;
                const viewedDocuments = typeof window !== "undefined" ? JSON.parse(localStorage.getItem('serviceManagementViewedDocuments') || '[]') : [];
                const isViewed = viewedDocuments.includes(docStep.id);
                return (
                  <SwiperSlide key={index} style={{ height: 'auto' }} className={styles.documentCard}>
                    <div className={styles.documentCardTop}>
                      {IconComponent && <IconComponent color="#000000" />}
                      {docStep.docNumber}
                    </div>
                    <div className={styles.documentCardMiddle}>
                      <div className={styles.documentCardMiddleTitle}>{docStep.title}</div>
                      <div className={styles.documentCardMiddleText}>{docStep.description}</div>
                    </div>
                    <Link className={styles.documentCardLink} href={docStep.link} onClick={() => markDocumentAsViewed(docStep.id)}>
                      {t("viewAll")}
                      <RightArrow />
                    </Link>
                  </SwiperSlide>
                )
              })}
            </Swiper>
            <div className={styles.productFeaturesSlideWrapper}>
              <div className={styles.productFeaturesCustomLeftArrow}></div>
            </div>
            <div className={styles.productFeaturesSlideWrapper2}>
              <div className={styles.productFeaturesCustomRightArrow}></div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export const getStaticProps: GetStaticProps = async ({ locale }) => ({
  props: {
    ...(await serverSideTranslations(locale || 'en', ['documentsTranslate', 'common'])),
  },
});

export default Documents;
